import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { get } from "lodash";
import { Formik } from "formik";
import { TextField } from "@mui/material";

import {
  UpdateAppNameSchema,
  ValidateAppNameSchema,
} from "../../globals/validations";
import { POONAWALLA_DIALOG_TYPES, PAGE_MODES } from "../../globals/constants";
import { createApplicationApi, updateApplicationApi } from "../../apis/portal";
import { showErrorToaster, showSuccessToaster } from "../../globals/toaster";
import { RefreshDataActions } from "../../redux/reducers/RefreshDataReducer";
import { verifyToken } from "../../globals/utils/jwtUtils";

import PoonaWallaModal from "../PoonaWallaModal";

const ApplicationModal = () => {
  const { authData } = useSelector(
    (store) => ({
      authData: store.auth.data || {},
    }),
    shallowEqual
  );

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const qp = new URLSearchParams(location.search);

  const initialFormValues = {
    name: qp.get("appName") || "",
    new_name: "",
  };
  const [pageMode, setPageMode] = useState(undefined);

  const handleUnmount = () => {
    // Remove Application Qparam & replace
    qp.delete("appmodal");
    qp.delete("appName");
    history.replace({
      search: qp.toString(),
    });
  };

  const handleValidateAppName = async (
    values,
    { setSubmitting, setFieldError, setStatus }
  ) => {
    try {
      setSubmitting(true);
      // Create Application
      if (pageMode === PAGE_MODES.CREATE) {
        const resp = await createApplicationApi({ ...values, platform: "WEB" });
        return history.push(
          `/list-api?application=${get(resp, "data.id", "")}`
        );
      } else {
        // Update Application
        await updateApplicationApi(
          {
            name: values.new_name,
          },
          qp.get("appmodal")
        );
        handleUnmount();
        dispatch(RefreshDataActions.SET_TO_REFRESH());
        showSuccessToaster("Application name changed.");
      }
    } catch (error) {
      const data = get(error, "data");
      if (get(data, "type") === POONAWALLA_DIALOG_TYPES.FIELD) {
        setFieldError(
          pageMode === PAGE_MODES.CREATE ? "name" : "new_name",
          get(data, "detail.name", "Application name not available.")
        );
      } else {
        showErrorToaster(
          get(data, "detail", "Application name not available.")
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (qp.get("appmodal")) {
      if (!get(authData, "idToken") || !verifyToken(get(authData, "idToken"))) {
        history.push("/signin");
      } else {
        // CREATE APPLICATION MODE
        if (qp.get("appmodal") === "new") {
          setPageMode(PAGE_MODES.CREATE);
        } else {
          // UPDATE APPLICATION MODE
          setPageMode(PAGE_MODES.EDIT);
        }
      }
    } else {
      setPageMode(undefined);
    }
  }, [location.search]);

  return (
    <>
      {pageMode && (
        <PoonaWallaModal
          size="md"
          handleHideModal={handleUnmount}
          isActive={Boolean(pageMode)}
          title={
            pageMode === PAGE_MODES.CREATE
              ? "Add New Application"
              : "Change Application Name"
          }
        >
          <div>
            <Formik
              initialValues={initialFormValues}
              validationSchema={
                pageMode === PAGE_MODES.CREATE
                  ? ValidateAppNameSchema()
                  : UpdateAppNameSchema()
              }
              onSubmit={handleValidateAppName}
            >
              {({
                values,
                errors,
                touched,
                setFieldValue,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <>
                  <form onSubmit={handleSubmit}>
                    {(pageMode !== PAGE_MODES.EDIT || qp.get("appName")) && (
                      <TextField
                        name="name"
                        label={
                          pageMode === PAGE_MODES.CREATE
                            ? "Application Name"
                            : "Existing Application Name"
                        }
                        error={errors.name && touched.name}
                        helperText={
                          errors.name && touched.name ? errors.name : ""
                        }
                        defaultValue={values.name}
                        value={values.name}
                        disabled={pageMode === PAGE_MODES.EDIT}
                        onChange={(e) => setFieldValue("name", e.target.value)}
                        onBlur={handleBlur}
                        variant="standard"
                        className="w-100"
                      />
                    )}
                    {pageMode === PAGE_MODES.EDIT && (
                      <TextField
                        name="new_name"
                        label="New Application Name"
                        error={errors.new_name && touched.new_name}
                        helperText={
                          errors.new_name && touched.new_name
                            ? errors.new_name
                            : ""
                        }
                        defaultValue={values.new_name}
                        value={values.new_name}
                        onChange={(e) =>
                          setFieldValue("new_name", e.target.value)
                        }
                        onBlur={handleBlur}
                        variant="standard"
                        className="w-100 mt-5"
                      />
                    )}
                    <button
                      type="submit"
                      className={`MainButton MainButton__primary outlined mt-5 ${
                        isSubmitting && "loading"
                      }`}
                      disabled={isSubmitting}
                    >
                      {pageMode === PAGE_MODES.CREATE
                        ? "Add Application"
                        : "Submit"}
                    </button>
                  </form>
                </>
              )}
            </Formik>
          </div>
        </PoonaWallaModal>
      )}
    </>
  );
};

export default ApplicationModal;
